/**
 * Utility functions for timing-related operations such as debouncing and throttling.
 */

export interface DebouncedFunction<T extends (...args: any[]) => void> {
  (...args: Parameters<T>): void
  flush: () => void
  cancel: () => void
}

/**
 * Creates a debounced function that delays the invocation of the provided function
 * until after the specified wait time has elapsed since the last time it was invoked.
 *
 * @param func - The function to debounce.
 * @param wait - The number of milliseconds to delay.
 * @returns A new debounced function with flush() and cancel() methods.
 *
 * @example
 * const debouncedLog = debounce(() => console.log('Debounced!'), 300);
 * window.addEventListener('resize', debouncedLog);
 * // Call debouncedLog.flush() to execute immediately
 * // Call debouncedLog.cancel() to cancel pending execution
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): DebouncedFunction<T> {
  let timeout: number | undefined
  let lastArgs: Parameters<T> | undefined
  let lastThis: any

  const debounced = function (this: any, ...args: Parameters<T>) {
    lastArgs = args
    lastThis = this
    if (timeout !== undefined) {
      clearTimeout(timeout)
    }
    timeout = window.setTimeout(() => {
      timeout = undefined
      lastArgs = undefined
      func.apply(lastThis, args)
    }, wait)
  } as DebouncedFunction<T>

  debounced.flush = () => {
    if (timeout !== undefined && lastArgs !== undefined) {
      clearTimeout(timeout)
      timeout = undefined
      const args = lastArgs
      lastArgs = undefined
      func.apply(lastThis, args)
    }
  }

  debounced.cancel = () => {
    if (timeout !== undefined) {
      clearTimeout(timeout)
      timeout = undefined
      lastArgs = undefined
    }
  }

  return debounced
}

/**
 * Creates a throttled function that only invokes the provided function
 * at most once per every specified limit of milliseconds.
 *
 * @param func - The function to throttle.
 * @param limit - The number of milliseconds to throttle invocations to.
 * @returns A new throttled function.
 *
 * @example
 * const throttledLog = throttle(() => console.log('Throttled!'), 300);
 * window.addEventListener('scroll', throttledLog);
 */
export function throttle<T extends (...args: any[]) => void>(func: T, limit: number): T {
  let inThrottle: boolean = false
  return function (this: any, ...args: any[]) {
    console.log('Throttling function', func.name, 'with limit', limit)
    if (!inThrottle) {
      console.log('Invoking throttled function', func.name)
      func.apply(this, args)
      inThrottle = true
      window.setTimeout(() => (inThrottle = false), limit)
    }
  } as T
}
